import { z } from 'zod';

/**
 * The answer shapes the platform can store, validate and render.
 *
 * A closed set rather than a free string, because a type is a **capability**,
 * not a label: each one decides the Zod schema an answer is validated against
 * (`answer-value-schema.ts`), the control the clients render, and whether the
 * question owns option rows. A type nobody can validate is a field that accepts
 * anything.
 *
 * Two of them look like refinements of others and are deliberately their own:
 *
 * - **`height`** is a number in **centimetres**, not a plain `number`. It has a
 *   fixed unit and a plausible human range, and clients present it as feet/inches
 *   or centimetres from the member's locale — a `number` question could express
 *   none of that, and matching on height (P16) needs one canonical unit.
 * - **`country`** is an ISO 3166-1 alpha-2 code, not a `single_select` with 250
 *   seeded option rows. The list is a code list, not operator content: making it
 *   options would hand staff the job of translating the world and put a fixed
 *   standard into editable rows (U16 — the platform is country-agnostic).
 *
 * `textarea` differs from `text` only in the control and the length ceiling,
 * which is exactly the sort of thing the clients must not guess.
 */
export const QUESTION_TYPES = [
  'text',
  'textarea',
  'number',
  'date',
  'boolean',
  'single_select',
  'multi_select',
  'height',
  'country',
] as const;

export const QuestionTypeSchema = z.enum(QUESTION_TYPES);

export type QuestionType = (typeof QUESTION_TYPES)[number];

/**
 * The types whose answers reference `question_options` rows — the only ones for
 * which an option list is meaningful, and the only ones a builder must refuse to
 * save empty.
 */
export const OPTION_BACKED_QUESTION_TYPES = ['single_select', 'multi_select'] as const;

export type OptionBackedQuestionType = (typeof OPTION_BACKED_QUESTION_TYPES)[number];

const OPTION_BACKED: ReadonlySet<string> = new Set<string>(OPTION_BACKED_QUESTION_TYPES);

/**
 * Whether this type stores its answer as a reference to option values.
 *
 * The one predicate the builder, the validator and the clients share — three
 * copies of "is it a select?" is how an option list ends up attached to a date
 * question.
 */
export function isOptionBacked(type: QuestionType): type is OptionBackedQuestionType {
  return OPTION_BACKED.has(type);
}

/** The types whose answer is an **array**; everything else stores a single value. */
export function isMultiValued(type: QuestionType): boolean {
  return type === 'multi_select';
}

const TYPE_SET: ReadonlySet<string> = new Set<string>(QUESTION_TYPES);

/** Narrows a plain string. Fail-closed: an unknown type has no validator at all. */
export function isQuestionType(value: string): value is QuestionType {
  return TYPE_SET.has(value);
}
